package com.gsb.vyapar.bhagya.presentation.location.util

import com.gsb.vyapar.bhagya.datasource.network.KtorClient
import com.gsb.vyapar.bhagya.datasource.network.response.NominatimResponse
import com.gsb.vyapar.bhagya.datasource.network.response.toLocation
import com.gsb.vyapar.bhagya.datasource.preference.Preference
import com.gsb.vyapar.bhagya.domain.Location
import com.gsb.vyapar.bhagya.domain.decodeLocationString
import com.gsb.vyapar.bhagya.domain.encodeLocationString
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.launchDefault
import com.gsb.vyapar.bhagya.interactor.util.hideLoading
import com.gsb.vyapar.bhagya.interactor.util.showError
import com.gsb.vyapar.bhagya.interactor.util.showLoading
import com.gsb.vyapar.bhagya.interactor.util.showSuccess
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.getState
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.launchMain
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.updateState
import com.gsb.vyapar.bhagya.presentation.dialog.Dialogs
import com.gsb.vyapar.bhagya.presentation.dialog.uistate.DialogUiState
import com.gsb.vyapar.bhagya.presentation.location.uistate.getLocationUiState
import com.gsb.vyapar.bhagya.presentation.location.uistate.updateLocationUiState
import com.gsb.vyapar.bhagya.requestDeviceLocation
import com.gsb.vyapar.bhagya.util.isPincodeError

fun showPincodeDialog() {
    updateState(getState().copy(dialogUiState = DialogUiState(dialogType = Dialogs.Location, onDeviceLocationClick = {
        showLoading()
        launchMain {
            val result = requestDeviceLocation()
            if (result != null) {
                launchDefault {
                    val nominatimResult = KtorClient.getNominatimLocation(result.latitude.toString(), result.longitude.toString())
                    if (nominatimResult.isSuccess) {
                        nominatimResult.getOrNull()?.let {
                            val location = it.toLocation()
                            updateLocationUiState(getLocationUiState().copy(pincode = it.address?.postcode.orEmpty(), location = location))
                            Preference.pincode = it.address?.postcode.orEmpty()
                            Preference.location = location.encodeLocationString()
                            hidePincodeDialog()
                            showSuccess("Location Saved")
                        }?: {
                            val location = Location(latitude = result.latitude, longitude =  result.longitude)
                            updateLocationUiState(getLocationUiState().copy(pincode = "", location = location))
                            Preference.pincode = ""
                            Preference.location = location.encodeLocationString()
                            hidePincodeDialog()
                            showSuccess("Location Saved")
                        }
                    } else {
                        val location = Location(latitude = result.latitude, longitude =  result.longitude)
                        updateLocationUiState(getLocationUiState().copy(pincode = "", location = location))
                        Preference.pincode = ""
                        Preference.location = location.encodeLocationString()
                        hidePincodeDialog()
                        showSuccess("Location Saved")
                    }
                    hideLoading()
                }
            } else {
                showError("Try Again, Error in fetching location")
                hideLoading()
            }
        }
    }, onEnterPincodeClick = {
        updateState(getState().copy(dialogUiState = DialogUiState(dialogType = Dialogs.Pincode, onSavePincode = {
            savePincode(it)
            hidePincodeDialog()
        })))
    }, onCloseClick = {
        hidePincodeDialog()
    })))
}

fun getDeviceLocationForBusiness(coordinates: (Location) -> Unit, nominatimResponse: (NominatimResponse) -> Unit) {
    updateState(
        getState().copy(
            dialogUiState = DialogUiState(
                title = "How would you like to provide your location?",
                message = "To register your business, we need its location. You can either use your device’s current location or manually enter your pincode to fetch the coordinates.",
                dialogType = Dialogs.Location,
                onDeviceLocationClick = {
                    showLoading()
                    launchMain {
                        val result = requestDeviceLocation()
                        if (result != null) {
                            launchDefault {
                                val nominatimResult = KtorClient.getNominatimLocation(result.latitude.toString(), result.longitude.toString())
                                if (nominatimResult.isSuccess) {
                                    nominatimResult.getOrNull()?.let {
                                        nominatimResponse(it)
                                        hidePincodeDialog()
                                        showSuccess("Business location Saved")
                                    }?: run {
                                        showError("Try Again, Error in fetching location")
                                    }
                                } else {
                                    showError("Try Again, Error in fetching location")
                                }
                                hideLoading()
                            }
                        } else {
                            showError("Try Again, Error in fetching location")
                            hideLoading()
                        }
                    }
                },
                onEnterPincodeClick = {
                    updateState(getState().copy(dialogUiState = DialogUiState(dialogType = Dialogs.AddBusinessPincode, title = "Set Business Location by Pincode", onSavePincode = { pincode ->
                        if (isPincodeError(pincode) || pincode.isEmpty()) {
                            showError("Enter valid pincode")
                        } else {
                            showLoading()
                            launchDefault {
                                val result = KtorClient.getLocation(pincode)
                                hideLoading()
                                if (result.isSuccess) {
                                    val location = result.getOrNull()
                                    if (location?.latitude != null && location.longitude != null) {
                                        coordinates(location.copy(pincode = pincode))
                                        hidePincodeDialog()
                                        showSuccess("Business location Saved")
                                    } else {
                                        showError("Invalid pincode")
                                    }
                                } else {
                                    showError("Invalid pincode")
                                }
                            }
                            hidePincodeDialog()
                        }
                    })))
                }, onCloseClick = {
                    hidePincodeDialog()
                })
        )
    )
}

fun hidePincodeDialog() {
    updateState(getState().copy(dialogUiState = null))
}

fun savePincode(pincode: String) {
    if (isPincodeError(pincode) || pincode.isEmpty()) {
        showError("Enter valid pincode")
        return
    }
    showLoading()
    launchDefault {
        val result = KtorClient.getLocation(pincode)
        hideLoading()
        if (result.isSuccess) {
            val location = result.getOrNull()
            updateLocationUiState(getLocationUiState().copy(pincode = pincode, location = location?.copy(pincode = pincode)))
            Preference.pincode = pincode
            location?.let { Preference.location = it.encodeLocationString() }
            hidePincodeDialog()
            showSuccess("Location Saved")
        } else {
            showError("Invalid pincode")
        }
    }
}

fun getSavedLocation(): Location? {
    val saved = getLocationUiState().location
    return saved ?: Preference.location.decodeLocationString().also {
        updateLocationUiState(getLocationUiState().copy(location = it))
    }
}

fun loadSavedPincode() {
    updateLocationUiState(
        getLocationUiState().copy(
            pincode = Preference.pincode,
            location = Preference.location.decodeLocationString()
        )
    )
}
